import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlinePlus,
  HiOutlineHeart,
  HiOutlineClock,
  HiOutlineShare,
  HiOutlineBookOpen,
  HiOutlineArrowRight,
} from "react-icons/hi2";
import { IoRestaurantOutline } from "react-icons/io5";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTheme } from "../../contexts/ThemeContext";
import apiConfig from "../../config/api";
import communityService from "../../services/communityService";
import authService from "../../services/AuthService";

const DASHBOARD_DONUT_COLORS = {
  recipes: "#3d2313",
  favorites: "#a85923",
  feed: "#e8ac5c",
};

const formatRelativeTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) return "à l'instant";
  if (diffHours < 24) return `il y a ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return date.toLocaleDateString("fr-FR", {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatNumber = (value) => Number(value || 0).toLocaleString("fr-FR");
const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const CHART_COLORS = {
  recipes: "#f59e0b",
  favorites: "#f97316",
  feed: "#fb923c",
  users: "#334155",
  active: "#f59e0b",
  admins: "#ea580c",
};

const formatShortWeekday = (date) =>
  date.toLocaleDateString("fr-FR", { weekday: "short" }).replace(".", "");

const getDateKey = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split("T")[0];
};

const buildCircleSegment = (value, total, radius, offset) => {
  if (!total || value <= 0) {
    return { dash: `0 ${2 * Math.PI * radius}`, nextOffset: offset };
  }

  const circumference = 2 * Math.PI * radius;
  const segment = (value / total) * circumference;
  return {
    dash: `${segment} ${circumference - segment}`,
    nextOffset: offset - segment,
  };
};

const UserDashboard = ({ user, token }) => {
  const { t } = useLanguage();
  const tt = (key, fallback) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };
  const { isDark } = useTheme();
  const donutTrackColor = isDark ? "rgba(255,255,255,0.15)" : "rgba(15,12,10,0.08)";
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [feed, setFeed] = useState([]);
  const [users, setUsers] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [monthlyTrends, setMonthlyTrends] = useState({
    topRecipes: [],
    topUsers: [],
  });
  const [communityStats, setCommunityStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const currentUser = useMemo(() => {
    if (user) return user;
    const storedUser = localStorage.getItem("user");
    if (!storedUser) return null;
    try {
      return JSON.parse(storedUser);
    } catch (parseError) {
      console.error("Erreur parsing user localStorage:", parseError);
      return null;
    }
  }, [user]);

  const isAdminUser = useMemo(
    () =>
      String(currentUser?.role || "").toLowerCase() === "admin" ||
      Boolean(currentUser?.is_admin || currentUser?.isAdmin),
    [currentUser],
  );

  const loadOverview = useCallback(async () => {
    const currentToken = token || localStorage.getItem("token");
    if (!currentToken) {
      setLoading(false);
      setError(tt("sessionExpiredPleaseLogin", "Session expirée. Merci de vous reconnecter."));
      return;
    }

    setLoading(true);
    setError("");

    try {
      if (isAdminUser) {
        const [usersRes, statsRes, topUsersRes, trendsRes] = await Promise.all([
          authService.getAllUsersAdmin().catch(() => ({ data: [] })),
          communityService.getCommunityStats().catch(() => null),
          communityService.getTopUsers(6, "score").catch(() => ({ users: [] })),
          communityService
            .getMonthlyTrends()
            .catch(() => ({ topRecipes: [], topUsers: [] })),
        ]);

        setUsers(Array.isArray(usersRes?.data) ? usersRes.data : []);
        setTopUsers(Array.isArray(topUsersRes?.users) ? topUsersRes.users : []);
        setMonthlyTrends({
          topRecipes: Array.isArray(trendsRes?.topRecipes)
            ? trendsRes.topRecipes
            : [],
          topUsers: Array.isArray(trendsRes?.topUsers) ? trendsRes.topUsers : [],
        });
        setCommunityStats(statsRes || null);
        setRecipes([]);
        setFavorites([]);
        setFeed([]);
      } else {
        const headers = { Authorization: `Bearer ${currentToken}` };
        const [recipesRes, favoritesRes, statsRes, feedRes] = await Promise.all([
          fetch(`${apiConfig.baseURL}/recipes?mine=1`, { headers }),
          fetch(`${apiConfig.baseURL}/favorites`, { headers }),
          communityService.getCommunityStats().catch(() => null),
          communityService.getFeed(6, 0).catch(() => ({ feed: [] })),
        ]);

        const [recipesBody, favoritesBody] = await Promise.all([
          recipesRes.ok ? recipesRes.json() : Promise.resolve({ data: [] }),
          favoritesRes.ok ? favoritesRes.json() : Promise.resolve({ data: [] }),
        ]);

        setRecipes(Array.isArray(recipesBody?.data) ? recipesBody.data : []);
        setFavorites(Array.isArray(favoritesBody?.data) ? favoritesBody.data : []);
        setCommunityStats(statsRes || null);
        setFeed(Array.isArray(feedRes?.feed) ? feedRes.feed : []);
        setUsers([]);
        setTopUsers([]);
        setMonthlyTrends({ topRecipes: [], topUsers: [] });
      }
    } catch (loadError) {
      console.error("Erreur chargement accueil:", loadError);
      setError(
        tt(
          "unableLoadActivityOverview",
          "Impossible de charger le récapitulatif de votre activité.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [isAdminUser, token, t]);

  useEffect(() => {
    if (currentUser) {
      loadOverview();
    } else {
      setLoading(false);
    }
  }, [currentUser, loadOverview]);

  const recipesThisWeek = useMemo(() => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return recipes.filter(
      (recipe) => recipe?.created_at && new Date(recipe.created_at) >= weekAgo,
    ).length;
  }, [recipes]);

  const firstName = currentUser?.first_name || currentUser?.username || "";

  const cards = [
    {
      id: "recipes",
      title: tt("myRecipes", "Mes recettes"),
      value: formatNumber(recipes.length),
      icon: IoRestaurantOutline,
      badge: recipesThisWeek > 0 ? `+${recipesThisWeek} cette semaine` : null,
    },
    {
      id: "favorites",
      title: tt("myFavorites", "Mes favoris"),
      value: formatNumber(favorites.length),
      icon: HiOutlineHeart,
    },
    {
      id: "feed",
      title: tt("recentActivities", "Activités récentes"),
      value: formatNumber(feed.length),
      icon: HiOutlineClock,
    },
    {
      id: "community",
      title: tt("sharedRecipes", "Recettes partagées"),
      value: formatNumber(communityStats?.shared_recipes),
      icon: HiOutlineShare,
    },
  ];

  const activitySeries = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const daily = new Map();
    for (let i = 6; i >= 0; i -= 1) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      daily.set(getDateKey(date), {
        label: formatShortWeekday(date),
        date,
        recipes: 0,
        feed: 0,
      });
    }

    recipes.forEach((recipe) => {
      if (!recipe?.created_at) return;
      const key = getDateKey(recipe.created_at);
      const entry = daily.get(key);
      if (entry) entry.recipes += 1;
    });

    feed.forEach((item) => {
      if (!item?.created_at) return;
      const key = getDateKey(item.created_at);
      const entry = daily.get(key);
      if (entry) entry.feed += 1;
    });

    return Array.from(daily.values()).map((item) => ({
      ...item,
      total: item.recipes + item.feed,
    }));
  }, [feed, recipes]);

  const maxActivity = useMemo(
    () => Math.max(1, ...activitySeries.map((item) => item.total)),
    [activitySeries],
  );

  const donutData = useMemo(
    () => [
      { id: "recipes", label: tt("myRecipes", "Mes recettes"), value: recipes.length },
      { id: "favorites", label: tt("myFavorites", "Mes favoris"), value: favorites.length },
      { id: "feed", label: tt("followedNews", "Suivis"), value: feed.length },
    ],
    [favorites.length, feed.length, recipes.length, t],
  );

  const donutTotal = useMemo(
    () => donutData.reduce((sum, item) => sum + item.value, 0),
    [donutData],
  );

  const adminComputedStats = useMemo(() => {
    const adminCount = users.filter(
      (item) => String(item.role || "").toLowerCase() === "admin",
    ).length;
    const userCount = users.length - adminCount;

    const now = new Date();
    const newUsersThisMonth = users.filter((item) => {
      if (!item?.created_at) return false;
      const createdDate = new Date(item.created_at);
      if (Number.isNaN(createdDate.getTime())) return false;
      return (
        createdDate.getMonth() === now.getMonth() &&
        createdDate.getFullYear() === now.getFullYear()
      );
    }).length;

    return { adminCount, userCount, newUsersThisMonth };
  }, [users]);

  const recentUsers = useMemo(
    () =>
      [...users]
        .filter((item) => item?.created_at)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5),
    [users],
  );

  const adminDonutData = useMemo(
    () => [
      { id: "admins", label: tt("admins", "Admins"), value: adminComputedStats.adminCount },
      { id: "users", label: tt("users", "Utilisateurs"), value: adminComputedStats.userCount },
      {
        id: "active",
        label: tt("active", "Actifs"),
        value: Number(communityStats?.active_users || 0),
      },
    ],
    [adminComputedStats.adminCount, adminComputedStats.userCount, communityStats, t],
  );

  const adminDonutTotal = useMemo(
    () => adminDonutData.reduce((sum, item) => sum + item.value, 0),
    [adminDonutData],
  );

  if (!currentUser) {
    return (
      <div className="bg-white dark:bg-white/10 backdrop-blur-lg rounded-2xl border border-ink-900/10 dark:border-white/20 p-8 text-center shadow-sm dark:shadow-none">
        <h1 className="text-2xl font-bold text-ink-900 dark:text-white mb-2">
          {t("unauthorizedAccess") || "Accès non autorisé"}
        </h1>
        <p className="text-ink-600 dark:text-white/90 mb-6">
          {t("mustBeLoggedIn") || "Vous devez être connecté."}
        </p>
        <button
          onClick={() => navigate("/auth")}
          className="px-5 py-2.5 bg-ember-600 hover:bg-ember-500 text-white rounded-xl font-semibold transition-colors"
        >
          {t("login") || "Se connecter"}
        </button>
      </div>
    );
  }

  if (isAdminUser) {
    return (
      <div className="space-y-6">
        <section className="relative overflow-hidden rounded-3xl border border-ink-900/10 dark:border-white/20 bg-gradient-to-br from-slate-900/90 via-slate-800/85 to-amber-700/70 p-6 sm:p-8">
          <div className="absolute -top-16 -right-10 h-40 w-40 rounded-full bg-white/20 blur-2xl" />
          <div className="absolute -bottom-20 left-0 h-48 w-48 rounded-full bg-amber-300/20 blur-2xl" />
          <div className="relative">
            <p className="text-xs uppercase tracking-wider text-white/80 font-semibold">
              {tt("adminArea", "Espace administration")}
            </p>
            <h1 className="text-2xl sm:text-4xl font-extrabold text-white mt-2">
              {tt("usersActivityTitle", "Activite de vos utilisateurs")}
            </h1>
            <p className="text-white/90 mt-3 max-w-2xl">
              {tt(
                "usersActivitySubtitle",
                "Vue globale pour suivre la croissance des comptes et la dynamique de votre communaute.",
              )}
            </p>
          </div>
        </section>

        {!!error && (
          <section className="bg-red-50 dark:bg-red-500/20 border border-red-200 dark:border-red-300/30 rounded-xl p-4 text-red-700 dark:text-red-100">
            {error}
          </section>
        )}

        <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <article className="rounded-2xl border border-ink-900/10 dark:border-white/20 bg-white dark:bg-white/10 backdrop-blur-md p-4 shadow-sm dark:shadow-lg">
            <p className="text-sm text-ink-500 dark:text-white/75">{tt("totalUsers", "Utilisateurs totaux")}</p>
            <p className="text-3xl font-extrabold text-ink-900 dark:text-white mt-1">
              {formatNumber(users.length)}
            </p>
            <p className="text-xs text-ink-400 dark:text-white/70 mt-2">{tt("registeredAccounts", "Comptes enregistres")}</p>
          </article>
          <article className="rounded-2xl border border-ink-900/10 dark:border-white/20 bg-white dark:bg-white/10 backdrop-blur-md p-4 shadow-sm dark:shadow-lg">
            <p className="text-sm text-ink-500 dark:text-white/75">{tt("activeUsers", "Utilisateurs actifs")}</p>
            <p className="text-3xl font-extrabold text-ink-900 dark:text-white mt-1">
              {formatNumber(communityStats?.active_users)}
            </p>
            <p className="text-xs text-ink-400 dark:text-white/70 mt-2">{tt("publishedAtLeastOneRecipe", "Ont deja publie une recette")}</p>
          </article>
          <article className="rounded-2xl border border-ink-900/10 dark:border-white/20 bg-white dark:bg-white/10 backdrop-blur-md p-4 shadow-sm dark:shadow-lg">
            <p className="text-sm text-ink-500 dark:text-white/75">{tt("newThisMonth", "Nouveaux ce mois")}</p>
            <p className="text-3xl font-extrabold text-ink-900 dark:text-white mt-1">
              {formatNumber(adminComputedStats.newUsersThisMonth)}
            </p>
            <p className="text-xs text-ink-400 dark:text-white/70 mt-2">{tt("monthlySignups", "Inscriptions mensuelles")}</p>
          </article>
          <article className="rounded-2xl border border-ink-900/10 dark:border-white/20 bg-white dark:bg-white/10 backdrop-blur-md p-4 shadow-sm dark:shadow-lg">
            <p className="text-sm text-ink-500 dark:text-white/75">{tt("sharedRecipes", "Recettes partagees")}</p>
            <p className="text-3xl font-extrabold text-ink-900 dark:text-white mt-1">
              {formatNumber(communityStats?.shared_recipes)}
            </p>
            <p className="text-xs text-ink-400 dark:text-white/70 mt-2">{tt("publishedInCommunity", "Publiees dans la communaute")}</p>
          </article>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <article className="xl:col-span-2 rounded-2xl border border-ink-900/10 dark:border-white/20 bg-white dark:bg-white/10 backdrop-blur-md p-5 shadow-sm dark:shadow-none">
            <div className="flex items-center justify-between gap-3 mb-5">
              <h2 className="text-lg font-semibold text-ink-900 dark:text-white">
                {tt("topUsersCommunityScore", "Top utilisateurs (score communaute)")}
              </h2>
              <span className="text-xs px-2.5 py-1 rounded-full bg-ember-100 text-ember-800 dark:bg-amber-500/40 dark:text-white">
                {tt("liveRanking", "Classement live")}
              </span>
            </div>
            {loading ? (
              <p className="text-ink-500 dark:text-white/70">{tt("loading", "Chargement...")}</p>
            ) : topUsers.length === 0 ? (
              <p className="text-ink-500 dark:text-white/70">
                {tt("noRankingData", "Aucune donnee de classement disponible.")}
              </p>
            ) : (
              <>
                <div className="h-48 sm:h-56 flex items-end gap-2 sm:gap-3">
                  {topUsers.map((item) => {
                    const maxScore = Math.max(
                      1,
                      ...topUsers.map((entry) => Number(entry.community_score || 0)),
                    );
                    const height = Math.max(
                      10,
                      (Number(item.community_score || 0) / maxScore) * 100,
                    );
                    return (
                      <div
                        key={item.id}
                        className="flex-1 flex flex-col items-center gap-2 min-w-0"
                      >
                        <div className="w-full max-w-12 h-40 sm:h-48 flex items-end">
                          <div
                            className="w-full rounded-xl border border-ink-900/10 dark:border-white/20 bg-amber-500/90"
                            style={{ height: `${height}%` }}
                            title={`${item.username}: ${formatNumber(item.community_score)}`}
                          />
                        </div>
                        <span className="text-[10px] sm:text-[11px] text-ink-500 dark:text-white/75 truncate max-w-full">
                          {item.username}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-ink-400 dark:text-white/70 mt-3">
                  {tt(
                    "scoreBasedOnRecipesFollowersInteractions",
                    "Score base sur recettes, followers et interactions.",
                  )}
                </p>
              </>
            )}
          </article>

          <article className="rounded-2xl border border-ink-900/10 dark:border-white/20 bg-white dark:bg-white/10 backdrop-blur-md p-5 shadow-sm dark:shadow-none">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ink-900 dark:text-white">
                {tt("accountsDistribution", "Repartition comptes")}
              </h2>
              <button
                type="button"
                onClick={loadOverview}
                className="text-xs px-3 py-1.5 rounded-lg bg-ink-100 hover:bg-ink-200 text-ink-700 dark:bg-white/20 dark:hover:bg-white/30 dark:text-white"
              >
                {tt("refresh", "Actualiser")}
              </button>
            </div>
            <div className="mt-4 flex justify-center">
              <div className="relative w-44 h-44">
                <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
                  <circle
                    cx="80"
                    cy="80"
                    r="54"
                    fill="none"
                    stroke={donutTrackColor}
                    strokeWidth="18"
                  />
                  {(() => {
                    const radius = 54;
                    let offset = 2 * Math.PI * radius;
                    return adminDonutData.map((item) => {
                      const segment = buildCircleSegment(
                        item.value,
                        adminDonutTotal,
                        radius,
                        offset,
                      );
                      offset = segment.nextOffset;
                      return (
                        <circle
                          key={item.id}
                          cx="80"
                          cy="80"
                          r={radius}
                          fill="none"
                          stroke={CHART_COLORS[item.id]}
                          strokeWidth="18"
                          strokeDasharray={segment.dash}
                          strokeDashoffset={offset}
                          strokeLinecap="round"
                        />
                      );
                    });
                  })()}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-extrabold text-ink-900 dark:text-white">
                    {formatNumber(adminDonutTotal)}
                  </span>
                  <span className="text-xs text-ink-400 dark:text-white/70">
                    {tt("trackingPoints", "points de suivi")}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {adminDonutData.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded-lg bg-ink-50 dark:bg-black/20 px-3 py-2"
                >
                  <span className="text-sm text-ink-700 dark:text-white/90 inline-flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: CHART_COLORS[item.id] }}
                    />
                    {item.label}
                  </span>
                  <span className="text-sm font-semibold text-ink-900 dark:text-white">
                    {formatNumber(item.value)}
                  </span>
                </div>
              ))}
            </div>
          </article>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <article className="rounded-2xl border border-ink-900/10 dark:border-white/20 bg-white dark:bg-white/10 backdrop-blur-md p-5 shadow-sm dark:shadow-none">
            <h2 className="text-lg font-semibold text-ink-900 dark:text-white mb-3">
              {tt("latestRegistrations", "Dernieres inscriptions")}
            </h2>
            {loading ? (
              <p className="text-ink-500 dark:text-white/70">{tt("loading", "Chargement...")}</p>
            ) : recentUsers.length === 0 ? (
              <p className="text-ink-500 dark:text-white/70">
                {tt("noNewUserFound", "Aucun nouvel utilisateur trouve.")}
              </p>
            ) : (
              <ul className="space-y-2">
                {recentUsers.map((item) => (
                  <li
                    key={item.id}
                    className="rounded-lg bg-ink-50 dark:bg-black/20 px-3 py-2 flex items-center justify-between gap-3"
                  >
                    <span className="text-sm text-ink-900 dark:text-white font-medium truncate">
                      {item.username || item.email}
                    </span>
                    <span className="text-xs text-ink-400 dark:text-white/70 shrink-0">
                      {formatDate(item.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </article>

          <article className="rounded-2xl border border-ink-900/10 dark:border-white/20 bg-white dark:bg-white/10 backdrop-blur-md p-5 shadow-sm dark:shadow-none">
            <h2 className="text-lg font-semibold text-ink-900 dark:text-white mb-3">
              {tt("creatorsOfTheMonth", "Createurs du mois")}
            </h2>
            {loading ? (
              <p className="text-ink-500 dark:text-white/70">{tt("loading", "Chargement...")}</p>
            ) : monthlyTrends.topUsers.length === 0 ? (
              <p className="text-ink-500 dark:text-white/70">
                {tt("noMonthlyTrendAvailable", "Aucune tendance disponible ce mois-ci.")}
              </p>
            ) : (
              <ul className="space-y-2">
                {monthlyTrends.topUsers.slice(0, 5).map((item) => (
                  <li
                    key={item.id}
                    className="rounded-lg bg-ink-50 dark:bg-black/20 px-3 py-2 flex items-center justify-between gap-3"
                  >
                    <span className="text-sm text-ink-900 dark:text-white font-medium truncate">
                      {item.username}
                    </span>
                    <span className="text-xs text-ink-400 dark:text-white/70 shrink-0">
                      {formatNumber(item.recipes_this_month)}{" "}
                      {tt("recipes", "recettes")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </article>
        </section>

        <section className="bg-white dark:bg-white/10 backdrop-blur-md border border-ink-900/10 dark:border-white/20 rounded-2xl p-5 shadow-sm dark:shadow-none">
          <h2 className="text-lg font-semibold text-ink-900 dark:text-white mb-3">
            {tt("adminQuickAccess", "Acces rapides admin")}
          </h2>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="px-4 py-2 rounded-lg bg-ember-600 hover:bg-ember-500 text-white text-sm"
            >
              {tt("manageUsers", "Gestion utilisateurs")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/community")}
              className="px-4 py-2 rounded-lg bg-ink-100 hover:bg-ink-200 text-ink-700 dark:bg-white/20 dark:hover:bg-white/30 dark:text-white text-sm"
            >
              {tt("viewCommunity", "Voir la communaute")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/recipes")}
              className="px-4 py-2 rounded-lg bg-ink-100 hover:bg-ink-200 text-ink-700 dark:bg-white/20 dark:hover:bg-white/30 dark:text-white text-sm"
            >
              {tt("browseRecipes", "Parcourir les recettes")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-ink-900 dark:text-white flex items-center gap-2">
            Bonjour {firstName}
            <span aria-hidden>👋</span>
          </h1>
          <p className="text-ink-500 dark:text-ink-400 mt-1">
            Voici ton résumé culinaire de la semaine
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/add-recipe")}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-ink-900 hover:bg-ink-800 dark:bg-ember-600 dark:hover:bg-ember-500 text-white font-semibold shadow-sm transition-colors shrink-0"
        >
          <HiOutlinePlus className="w-5 h-5" />
          Nouvelle Recette
        </button>
      </section>

      {!!error && (
        <section className="bg-red-50 dark:bg-red-500/20 border border-red-200 dark:border-red-300/30 rounded-xl p-4 text-red-700 dark:text-red-100">
          {error}
        </section>
      )}

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map((card) => {
          const CardIcon = card.icon;
          return (
            <article
              key={card.id}
              className="rounded-2xl border border-ink-900/10 dark:border-ink-700 bg-white dark:bg-ink-800 p-4 shadow-sm"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-ember-50 dark:bg-ember-500/15 text-ember-700 dark:text-ember-300">
                  <CardIcon className="w-5 h-5" />
                </span>
                {card.badge && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400">
                    {card.badge}
                  </span>
                )}
              </div>
              <p className="text-sm text-ink-500 dark:text-ink-400">{card.title}</p>
              <p className="text-3xl font-extrabold text-ink-900 dark:text-white mt-0.5">
                {card.value}
              </p>
            </article>
          );
        })}
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <article className="xl:col-span-2 rounded-2xl border border-ink-900/10 dark:border-ink-700 bg-white dark:bg-ink-800 p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-ink-900 dark:text-white mb-5">
            Activité des 7 derniers jours
          </h2>

          <div className="h-44 sm:h-52 flex items-end gap-2 sm:gap-3">
            {activitySeries.map((item) => {
              const height = Math.max(6, (item.total / maxActivity) * 100);
              return (
                <div key={item.label} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full h-36 sm:h-44 flex items-end">
                    <div
                      className="w-full rounded-t-2xl transition-all duration-300"
                      style={{
                        height: `${height}%`,
                        background:
                          "linear-gradient(180deg, #dd9143 0%, #a85923 100%)",
                      }}
                      title={`${item.label}: ${item.total}`}
                    />
                  </div>
                  <span className="text-[11px] uppercase text-ink-400 dark:text-ink-500">
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </article>

        <article className="rounded-2xl border border-ink-900/10 dark:border-ink-700 bg-white dark:bg-ink-800 p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-ink-900 dark:text-white mb-2">
            Répartition de ton activité
          </h2>

          <div className="mt-2 flex justify-center">
            <div className="relative w-40 h-40">
              <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
                <circle
                  cx="80"
                  cy="80"
                  r="54"
                  fill="none"
                  stroke={donutTrackColor}
                  strokeWidth="18"
                />
                {(() => {
                  const radius = 54;
                  let offset = 2 * Math.PI * radius;
                  return donutData.map((item) => {
                    const segment = buildCircleSegment(
                      item.value,
                      donutTotal,
                      radius,
                      offset,
                    );
                    offset = segment.nextOffset;
                    return (
                      <circle
                        key={item.id}
                        cx="80"
                        cy="80"
                        r={radius}
                        fill="none"
                        stroke={DASHBOARD_DONUT_COLORS[item.id]}
                        strokeWidth="18"
                        strokeDasharray={segment.dash}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                      />
                    );
                  });
                })()}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-extrabold text-ink-900 dark:text-white">
                  {donutTotal > 0 ? "100%" : "0%"}
                </span>
                <span className="text-[10px] uppercase tracking-wide text-ink-400 dark:text-ink-500">
                  Total
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {donutData.map((item) => {
              const percentage =
                donutTotal > 0 ? Math.round((item.value / donutTotal) * 100) : 0;
              return (
                <div key={item.id} className="flex items-center justify-between px-1">
                  <span className="text-sm text-ink-700 dark:text-ink-200 inline-flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: DASHBOARD_DONUT_COLORS[item.id] }}
                    />
                    {item.label}
                  </span>
                  <span className="text-sm font-semibold text-ink-900 dark:text-white">
                    {percentage}%
                  </span>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <article className="rounded-2xl border border-ink-900/10 dark:border-ink-700 bg-white dark:bg-ink-800 p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-ink-900 dark:text-white mb-3">
            Dernières Activités
          </h2>
          {loading ? (
            <p className="text-ink-500 dark:text-ink-400 text-sm">Chargement...</p>
          ) : feed.length === 0 ? (
            <p className="text-ink-500 dark:text-ink-400 text-sm">
              Aucune activité récente pour le moment.
            </p>
          ) : (
            <ul className="space-y-3">
              {feed.slice(0, 4).map((item) => (
                <li key={item.id} className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-ember-50 dark:bg-ember-500/15 text-ember-700 dark:text-ember-300 shrink-0">
                    <HiOutlineBookOpen className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm text-ink-900 dark:text-white font-medium truncate">
                      {item.title || "Nouvelle recette"}
                    </p>
                    <p className="text-xs text-ink-400 dark:text-ink-500 mt-0.5">
                      {formatRelativeTime(item.created_at)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="lg:col-span-2 rounded-2xl border border-ink-900/10 dark:border-ink-700 bg-white dark:bg-ink-800 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-ink-900 dark:text-white">
              Mes Recettes Récentes
            </h2>
            <button
              type="button"
              onClick={() => navigate("/recipes")}
              className="text-sm font-medium text-ember-700 dark:text-ember-300 hover:text-ember-600 dark:hover:text-ember-200 inline-flex items-center gap-1"
            >
              Voir tout
              <HiOutlineArrowRight className="w-4 h-4" />
            </button>
          </div>
          {loading ? (
            <p className="text-ink-500 dark:text-ink-400 text-sm">Chargement...</p>
          ) : recipes.length === 0 ? (
            <p className="text-ink-500 dark:text-ink-400 text-sm">
              Vous n'avez pas encore de recette. Commencez par en ajouter une.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {recipes.slice(0, 3).map((recipe) => {
                const rating = Number(recipe.average_rating) || 0;
                const time =
                  (Number(recipe.prep_time) || 0) + (Number(recipe.cook_time) || 0);
                return (
                  <button
                    type="button"
                    key={recipe.id}
                    onClick={() => navigate(`/recipe/${recipe.id}`)}
                    className="text-left rounded-xl overflow-hidden border border-ink-900/10 dark:border-ink-700 bg-white dark:bg-ink-900 hover:shadow-md transition-shadow"
                  >
                    <div className="relative aspect-square bg-ink-100 dark:bg-ink-700">
                      {recipe.image_url && (
                        <img
                          src={recipe.image_url}
                          alt={recipe.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {rating > 0 && (
                        <span className="absolute top-2 right-2 text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-white/90 text-ink-900 shadow-sm">
                          {rating.toFixed(1)} ★
                        </span>
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="text-sm font-semibold text-ink-900 dark:text-white truncate">
                        {recipe.title || "Recette sans titre"}
                      </p>
                      <p className="text-xs text-ink-400 dark:text-ink-500 mt-0.5">
                        {time > 0 ? `${time} min` : "-"}
                        {recipe.difficulty ? ` · ${recipe.difficulty}` : ""}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </article>
      </section>
    </div>
  );
};

export default UserDashboard;
